import { api } from './api';

export interface FeedEvidence {
  signal_id: string;
  platform: string;
  url: string | null;
  text: string;
}

export interface FeedCluster {
  cluster_id: string;
  cluster_version: number;
  title: string;
  summary: string | null;
  topics: string[];
  entities: string[];
  platforms: string[];
  category: string | null;
  region: string | null;
  language: string | null;
  signal_count: number;
  first_seen_at: string;
  last_seen_at: string;
  velocity: { '1h': number; '6h': number; '24h': number };
  trend_score: number;
  score_explanation: {
    version?: string;
    components?: Record<string, number>;
    weights?: Record<string, number>;
    window_counts?: Record<string, number>;
  };
  evidence: FeedEvidence[];
  schema_version: number;
}

export interface FeedResponse {
  items: FeedCluster[];
  next_cursor: string | null;
  as_of: string;
}

export interface FeedSignal {
  id: string;
  platform: string;
  type: string;
  text: string;
  url: string | null;
  published_at: string | null;
  captured_at: string;
  similarity: number;
  membership_reason: Record<string, unknown>;
}

export interface ConnectorStatus {
  id: string;
  display_name: string;
  enabled: boolean;
  capabilities: Record<string, boolean>;
  health?: {
    status: string;
    message: string;
    checked_at?: string;
    latency_ms?: number | null;
  };
}

export interface FeedFilters {
  platforms?: string[];
  topic?: string;
  entity?: string;
  category?: string;
  region?: string;
  language?: string;
  minScore?: number;
  window?: string;
  cursor?: string | null;
}

function feedQuery(filters: FeedFilters): string {
  const query = new URLSearchParams();
  for (const platform of filters.platforms ?? []) query.append('platform', platform);
  if (filters.topic) query.append('topic', filters.topic);
  if (filters.entity) query.append('entity', filters.entity);
  if (filters.category) query.set('category', filters.category);
  if (filters.region) query.set('region', filters.region);
  if (filters.language) query.set('language', filters.language);
  query.set('min_score', String(filters.minScore ?? 0));
  query.set('window', filters.window ?? '24h');
  if (filters.cursor) query.set('cursor', filters.cursor);
  return query.toString();
}

export const feedDriveService = {
  getFeed: (filters: FeedFilters = {}): Promise<FeedResponse> =>
    api.get<FeedResponse>(`/feeddrive/feed?${feedQuery(filters)}`),
  getClusterSignals: (clusterId: string): Promise<FeedSignal[]> =>
    api.get<FeedSignal[]>(`/feeddrive/clusters/${clusterId}/signals`),
  getConnectors: (): Promise<ConnectorStatus[]> =>
    api.get<ConnectorStatus[]>('/feeddrive/connectors'),
};
